<!-- Parsec Cloud (https://parsec.cloud) Copyright (c) AGPL-3.0-only 2016-present Scille SAS -->

<template>
  <div class="workspace-user-role">
    <div class="content-user">
      <user-avatar-name
        :user-avatar="user.humanHandle.label"
        :user-name="user.humanHandle.label"
        :user-description="getProfileTranslationKey(user.profile)"
      />
      <span
        v-if="isCurrentUser"
        class="body content-user--self"
      >
        {{ $msTranslate('UsersPage.currentUser') }}
      </span>
    </div>

    <ms-dropdown
      ref="dropdown"
      class="dropdown"
      :options="options"
      :disabled="disabled"
      :default-option-key="role"
      :appearance="MsAppearance.Outline"
      @change="onRoleChanged(user, $event.option, $event.oldOption)"
    />
  </div>
</template>

<script setup lang="ts">
import { UserAvatarName } from '@/components/users';
import { canChangeRole } from '@/components/workspaces/utils';
import { UserProfile, UserTuple, WorkspaceRole } from '@/parsec';
import { getProfileTranslationKey, getWorkspaceRoleTranslationKey } from '@/services/translation';
import { MsAppearance, MsDropdown, MsOption, MsOptions } from 'megashark-lib';
import { computed, useTemplateRef } from 'vue';

const props = defineProps<{
  user: UserTuple;
  role: WorkspaceRole | null;
  disabled?: boolean;
  clientProfile: UserProfile;
  clientRole: WorkspaceRole | null;
  isCurrentUser?: boolean;
}>();

const emits = defineEmits<{
  (e: 'roleUpdate', user: UserTuple, oldRole: WorkspaceRole | null, newRole: WorkspaceRole | null, reject: () => void): void;
}>();

const dropdownRef = useTemplateRef<InstanceType<typeof MsDropdown>>('dropdown');

const options = computed((): MsOptions => {
  return new MsOptions(
    [WorkspaceRole.Owner, WorkspaceRole.Manager, WorkspaceRole.Contributor, WorkspaceRole.Reader, null].map(
      (role: WorkspaceRole | null) => {
        return {
          key: role,
          label: getWorkspaceRoleTranslationKey(role).label,
          description: getWorkspaceRoleTranslationKey(role).description,
          disabled: !canChangeRole(props.clientProfile, props.user.profile, props.clientRole, props.role, role).authorized,
          disabledReason: canChangeRole(props.clientProfile, props.user.profile, props.clientRole, props.role, role).reason,
        };
      },
    ),
  );
});

function onRoleChanged(user: UserTuple, newRoleOption: MsOption, oldRoleOption?: MsOption): void {
  if (oldRoleOption && newRoleOption.key === oldRoleOption.key) {
    return;
  }
  emits('roleUpdate', user, oldRoleOption ? oldRoleOption.key : null, newRoleOption.key, () => {
    dropdownRef.value?.setCurrentKey(oldRoleOption ? oldRoleOption.key : null);
  });
}
</script>

<style scoped lang="scss">
.workspace-user-role {
  padding: 0.625rem 0.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    display: block;
    width: calc(100% - 3rem);
    border-bottom: 1px solid var(--parsec-color-light-secondary-medium);
    bottom: 0;
    right: 0;
  }
}

.dropdown {
  flex-shrink: 0;
}

.content-user {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: flex;
  gap: 0.25rem;

  &--self {
    color: var(--parsec-color-light-secondary-grey);
  }
}
</style>
